"use client";

import "video.js/dist/video-js.css";
import "./VideoJS-theme.css";

import { SpecialEvents, trackEvent } from "@punchup/analytics";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDeviceDetection, useIsMobile } from "@punchup/utils/hooks";

import { Database } from "@punchup/data/supabase.types";
import Player from "video.js/dist/types/player";
import { SpecialRow } from "@punchup/data/legacy/specials";
import { VideoComedianContext } from "@punchup/data/legacy/videoPlayback";
import { VideoLogger } from "../VideoLogger";
import { usePodcastPlaybackContextSync } from "../PodcastPlaybackContext";
import { VideoPlayerSurface } from "../../types";
import VideoOverlays from "./VideoOverlays";
import { cn } from "@punchup/utils/cn";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { getViewingTokenForSpecial } from "../VideoURL";
import { logException } from "@punchup/sentry";
import { updateUserPlaybackLocation } from "@punchup/data/client/user/specials";
import { useInterval } from "@punchup/utils/hooks";
import { useRouter } from "next/navigation";
import videojs from "video.js";

// play() returns a Promise that rejects when playback can't start (most
// commonly autoplay blocked by browser policy). Swallowing it silently hides
// real failures and produces unhandled-rejection warnings; log it instead.
// `context` identifies which playback path triggered the call.
const playWithErrorLogging = (player: Player, context: string) => {
  player.play()?.catch((err) => {
    logException(err, { tags: { source: "VideoJS", playContext: context } });
  });
};

type VideoProps = {
  bigPlayButton?: boolean;
  lastPlaybackLocation: number;
  special: SpecialRow | null;
  isPreview: boolean;
  surface: VideoPlayerSurface;
  poster: string;
  context?: VideoComedianContext;
  muted?: boolean;
  handleMutedChange?: (value: boolean) => void;
  src: string;
  inline?: boolean;
  onSpecialChange?: (special: SpecialRow) => void;
  className?: string;
  disableOverlays?: boolean;
  playing?: boolean;
  onPlayStatusChanged?: (status: "PLAYING" | "PAUSED" | "ENDED") => void;
  disableLoop?: boolean;
  disableControls?: boolean;
  nextUpSpecial?: SpecialRow | null;
  secondaryNextSpecial?: SpecialRow | null;
  showEndOverlay?: boolean;
  onCancel?: () => void;
  /**
   * Override for "play next". When provided, the host owns play-next (it changes
   * the `src`/`special` props, which this component then applies to the live
   * player — see the source-swap effect). Used by the persistent player so the
   * SAME element keeps playing across navigation. When omitted, the built-in
   * in-place swap runs.
   */
  onPlayNext?: (special: SpecialRow) => void;
  isVertical?: boolean | null;
  playerProps?: {
    showSkipButtons?: boolean;
    showTimeRemaining?: boolean;
    showPlaybackRate?: boolean;
    showSubtitles?: boolean;
    showVolumePanel?: boolean;
    showFullscreenToggle?: boolean;
  };
  playerRef?: React.MutableRefObject<Player | null>;
  onDurationChange?: (durationSeconds: number) => void;
  onTimeUpdate?: (currentTime: number) => void;
};

const sourceUrl = (src: string) =>
  `https://${process.env.CLOUDFLARE_CUSTOMER_SUBDOMAIN}/${src}/manifest/video.m3u8`;

// Tracks which special each live player is currently bound to, for host-driven
// (persistent player) source swaps. Keyed on the player — which the host owns
// and which OUTLIVES the VideoJS component across navigation — so the swap
// bookkeeping survives a component remount that resets any per-instance ref.
// A WeakMap lets the entry be collected when the player is disposed.
const appliedSpecialByPlayer = new WeakMap<Player, string>();

/**
 * Whether an "up next" pick can be played by swapping the video source IN PLACE
 * rather than navigating. In-place playback is only safe for the SAME comedian
 * currently playing. A different comedian — OR an unknown current comedian
 * (`current` is null, e.g. the persistent player has no special context) — must
 * navigate instead: navigation lands on the correct page chrome and avoids
 * silently dropping picks the viewer can't access in place. The null-current
 * case is intentional, not an accident of `!==` comparing against `undefined`.
 */
export const canPlayInPlace = (
  next: Pick<SpecialRow, "comedian_id">,
  current: Pick<SpecialRow, "comedian_id"> | null | undefined
): boolean =>
  current?.comedian_id != null && next.comedian_id === current.comedian_id;

const defaultPlayerProps = {
  showSkipButtons: true,
  showTimeRemaining: true,
  showPlaybackRate: true,
  showSubtitles: true,
  showVolumePanel: true,
  showFullscreenToggle: true,
};

const VideoJS = ({
  bigPlayButton = true,
  lastPlaybackLocation,
  special,
  isPreview,
  surface,
  poster,
  context,
  muted = false,
  handleMutedChange = () => {},
  src,
  inline,
  onSpecialChange,
  className,
  disableOverlays,
  playing,
  onPlayStatusChanged,
  disableLoop,
  disableControls,
  nextUpSpecial,
  secondaryNextSpecial,
  showEndOverlay,
  onCancel,
  onPlayNext: onPlayNextOverride,
  isVertical,
  playerProps,
  playerRef: externalPlayerRef,
  onDurationChange,
  onTimeUpdate,
}: VideoProps) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [lastTime, setLastTime] = useState<number | null>(null);
  const supabase = createClientComponentClient<Database>();
  const router = useRouter();
  const { device, isLandscape } = useDeviceDetection();
  const isMobile = useIsMobile();

  const videoRef = useRef<HTMLDivElement>(null);
  const internalPlayerRef = useRef<Player | null>(null);
  const playerRef = externalPlayerRef || internalPlayerRef;
  const loggerRef = useRef<VideoLogger | null>(null);
  // Bump after the logger is created so usePodcastPlaybackContextSync re-runs
  // with the new logger reference and attaches podcast context before user
  // interactions (play / pause / view) can fire events. Without this, the
  // hook's effect would see loggerRef.current === null on first render and
  // never re-run, since ref mutations don't trigger re-renders.
  const [, setLoggerVersion] = useState(0);
  usePodcastPlaybackContextSync(loggerRef.current);
  const overlaysRef = useRef<HTMLDivElement | null>(null);
  const specialRef = useRef<SpecialRow | null>(special);

  // The player's event listeners are bound ONCE when the player is created, so a
  // raw `onPlayStatusChanged` closure would freeze to the first render — e.g. the
  // "ended" handler would fire the mount-time callback, which captured stale
  // `nextUp` recommendations (before the autoplay hook had loaded). Route the
  // callback through a ref that we keep current so listeners always call the
  // latest version.
  const onPlayStatusChangedRef = useRef(onPlayStatusChanged);
  useEffect(() => {
    onPlayStatusChangedRef.current = onPlayStatusChanged;
  }, [onPlayStatusChanged]);

  // Host-driven mode (persistent player): the host changes the `src` prop to play
  // a new special on the SAME element (preserving play-authorization). The
  // built-in flow swaps the source itself, so this only runs when a host has
  // taken over play-next. Skips the first run — the create effect handles the
  // initial source.
  const isHostDriven = Boolean(onPlayNextOverride);
  // The special the live PLAYER is currently bound to. A swap is driven by the
  // host pointing us at a DIFFERENT special — that is the true trigger, not a
  // bare `src` change (a token could be refreshed for the same special without
  // meaning "start a new logging session"). Gating on `special.id` lets the
  // effect depend on everything it reads (no exhaustive-deps suppression, so the
  // new logger is always built from fresh props) while firing the swap only on a
  // genuine special change.
  //
  // This bookkeeping is keyed on the PLAYER (via `appliedSpecialByPlayer`), not a
  // component-instance ref. The persistent player's `playerRef` is owned by the
  // host and OUTLIVES this component — across navigation the host can re-render
  // (or remount) VideoJS while the same underlying player keeps playing. A
  // per-instance ref resets to `null` on any such remount, which made the old
  // "first run → assume the create-effect already loaded this special" guard
  // wrongly skip the swap, leaving the PREVIOUS special's video loaded on the
  // element. Tagging the player itself survives the remount, so we always know
  // what it's actually showing. The create effect (below) tags the player with
  // the special it builds it for.
  useEffect(() => {
    if (!isHostDriven) return;
    const player = playerRef.current;
    if (!player || !src || !special) return;

    // The player is already bound to this special: the create effect just built
    // it for `special`, or we already swapped to it (incl. a same-special token
    // refresh). Nothing to do — don't tear down the session or reset playback.
    if (appliedSpecialByPlayer.get(player) === special.id) return;

    appliedSpecialByPlayer.set(player, special.id);

    // The persistent player is about to play a DIFFERENT special on the same
    // element. End the previous logging session and construct a fresh one so
    // events attribute to the special now playing and watchtime/state reset —
    // without this the logger stays bound to the special the element first
    // loaded, so every subsequent special's events are mis-attributed to the
    // first one.
    //
    // We deliberately do NOT call the new logger's start() here. start() logs
    // SPECIAL_CLICKED_PLAY on every call, and the player's "play" event handler
    // (which reads the live `loggerRef`) already starts the session when
    // playback resumes on the swapped source. Starting it here too would emit a
    // duplicate SPECIAL_CLICKED_PLAY and double-count the play. Letting the play
    // event own start() also means the session begins only once playback
    // actually starts (e.g. not if autoplay is blocked) — matching the built-in
    // in-place swap path.
    loggerRef.current?.end("special-changed");
    loggerRef.current = new VideoLogger(
      special,
      isPreview,
      surface,
      null,
      supabase
    );
    setLoggerVersion((v) => v + 1);

    player.src(sourceUrl(src));
    playWithErrorLogging(player, "host-driven-swap");
  }, [src, isHostDriven, special, isPreview, surface, supabase, playerRef]);

  // Keep the player's poster in sync with the prop for host-driven (persistent
  // player) swaps. The create effect sets `player.poster()` ONCE — and bails on
  // every later run because the player already exists — so without this the
  // poster stays frozen on the first special the element loaded. video.js only
  // surfaces the poster before playback / between sources, which is exactly the
  // loading gap during an autoplay swap; a stale poster there flashed the FIRST
  // special's image before each new video painted. Harmless while playing.
  useEffect(() => {
    if (!isHostDriven) return;
    const player = playerRef.current;
    if (!player || !poster) return;
    player.poster(poster);
  }, [isHostDriven, poster, playerRef]);

  const mergedPlayerProps = { ...defaultPlayerProps, ...playerProps };

  useInterval(
    () => {
      const currentTime = playerRef?.current?.currentTime();
      if (currentTime) {
        updateLocation(currentTime);
      }
    },
    // Store playback location every 5 secs, if video is playing
    isPlaying ? 5000 : null
  );

  const updateLocation = useCallback(
    async (location: number) => {
      // Adding null-check for location as occasionally currentTime appears to be null even though videoRef.current is defined
      // Also ensure that the video is not a preview, which shouldn't count as 'views'
      if (location && special && !context?.isPreview) {
        await updateUserPlaybackLocation(special.id, location);
      }
    },
    [special, context?.isPreview]
  );

  const source = sourceUrl(src);

  useEffect(() => {
    if (!videoRef.current) return;
    // Player is already initialized — all options in this effect are only used
    // during construction. Re-running would create a second VideoJS instance on
    // top of the existing one, corrupting the DOM.
    if (playerRef.current) return;

    const initialMutedState =
      muted !== undefined ? muted : (context?.isPreview ?? false);
    // Set up a simplified control bar for mobile and a fuller one for desktop.

    const controlBarOptions = {
      skipButtons: mergedPlayerProps?.showSkipButtons
        ? {
            forward: 30,
            backward: 30,
          }
        : undefined,
      volumePanel: {
        inline: false,
      },
      children: [
        "playToggle",
        mergedPlayerProps?.showSkipButtons && "skipBackward",
        mergedPlayerProps?.showSkipButtons && "skipForward",
        "progressControl",
        mergedPlayerProps?.showTimeRemaining && "remainingTimeDisplay",
        mergedPlayerProps?.showPlaybackRate && "playbackRateMenuButton",
        mergedPlayerProps?.showSubtitles && "subtitlesButton",
        mergedPlayerProps?.showVolumePanel && "volumePanel",
        mergedPlayerProps?.showFullscreenToggle && "fullscreenToggle",
      ],
    };

    const options = {
      bigPlayButton: bigPlayButton,
      playbackRates: [0.75, 0.9, 1, 1.1, 1.25, 1.5, 2],
      playsinline: true,
      controls: !disableControls,
      fluid: false,
      muted: initialMutedState,
      loop: disableLoop ? false : context?.isPreview || false,
      autoplay:
        new URLSearchParams(window.location.search).get("autoplay") === "1" ||
        context?.isPreview ||
        context?.isLoggedIn,
      controlBar: controlBarOptions,
      sources: [
        {
          src: source,
          type: "application/x-mpegURL",
        },
      ],
    };

    if (!overlaysRef.current) {
      overlaysRef.current = document.querySelector("#vjs-punchup-overlays");
    }

    if (!playerRef.current) {
      // Build a DOM video element
      const videoElement = document.createElement("video-js");

      if (inline) {
        videoElement.classList.add("vjs-inline");
      }
      videoElement.classList.add("vjs-show-big-play-button-on-pause");
      videoElement.classList.add("vjs-punchup");
      videoElement.classList.add("vjs-16-9");

      if (isVertical) {
        videoElement.classList.add("vjs-vertical");
      }

      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, options));

      if (special) {
        // Tag the player with the special it's being built for, so the
        // host-driven swap effect knows the live element is already showing this
        // special and won't (a) skip a real swap after a remount or (b) reset
        // playback by re-swapping the initial source. See `appliedSpecialByPlayer`.
        //
        // Only host-driven (persistent-player) instances participate in the
        // WeakMap: the swap effect bails early on `!isHostDriven` and never reads
        // the entry otherwise, so an ordinary inline player has no reason to write
        // shared state. Scoping the write keeps that bookkeeping fully contained
        // to the persistent player and out of every other VideoJS render.
        if (isHostDriven) {
          appliedSpecialByPlayer.set(player, special.id);
        }
        loggerRef.current = new VideoLogger(
          special,
          isPreview,
          surface,
          null,
          supabase
        );
        setLoggerVersion((v) => v + 1);
      }

      const overlays = overlaysRef.current;
      if (!overlays) {
        console.warn("No overlays found");
      } else {
        videoElement.appendChild(overlays);
      }

      player.poster(poster);

      let firstCanPlayThrough = false;
      player.on("canplaythrough", () => {
        if (firstCanPlayThrough) return;
        firstCanPlayThrough = true;
        // We need to wait until canplaythrough otherwise mobile Safari screws up
        // when setting the currentTime, but this event gets fired multiple times
        // and we only care about the first one, so keep track if we've seen the
        // first event or not
        if (lastPlaybackLocation) {
          const duration = player.duration();
          if (
            playerRef?.current &&
            duration != null &&
            lastPlaybackLocation / duration < 0.95
          ) {
            player.currentTime(lastPlaybackLocation);
          }
        }
        if (special !== null) {
          trackEvent(SpecialEvents.SPECIAL_INITIALIZED, {
            special_id: loggerRef.current?.special.id,
            isPreview,
            location: lastPlaybackLocation || 0,
          });
        }
      });

      player.on("durationchange", () => {
        const d = player.duration();
        if (d && isFinite(d)) {
          loggerRef.current?.setDuration(d);
          onDurationChange?.(d);
        }
      });

      player.on("timeupdate", () => {
        const t = player.currentTime();
        if (t !== undefined && onTimeUpdate) {
          onTimeUpdate(t);
        }
      });

      player.on("ready", () => {
        const controlBar = player.getChild("ControlBar");
        const skipFwd = controlBar?.getChild("SkipForward");
        const skipBack = controlBar?.getChild("SkipBackward");
        const volumePanel = controlBar?.getChild("VolumePanel");
        skipFwd?.on("click", () => loggerRef.current?.skipForward());
        skipBack?.on("click", () => loggerRef.current?.skipBackward());
        volumePanel?.on("click", () => loggerRef.current?.volumeChanged());
      });

      player.on("ratechange", () => {
        loggerRef.current?.playbackRateChanged(player.playbackRate());
      });

      player.on("fullscreenchange", () => {
        loggerRef.current?.fullscreenChanged(player.isFullscreen());
      });

      player.on("texttrackchange", () => {
        const tracks = player.textTracks();
        let active: TextTrack | null = null;

        // Find active text track
        for (let i = 0; i < tracks.tracks_.length; i++) {
          const t = tracks.tracks_[i];
          if (
            (t.kind === "captions" || t.kind === "subtitles") &&
            t.mode === "showing"
          ) {
            active = t;
            break;
          }
        }

        loggerRef.current?.textTrackChanged(active);
      });

      player.on("play", () => {
        setIsPlaying(true);
        onPlayStatusChangedRef.current?.("PLAYING");
        if (player.muted()) {
          loggerRef.current?.mutedChanged(true);
        }
        loggerRef.current?.start(player.currentTime() || 0);
      });

      player.on("pause", () => {
        setIsPlaying(false);
        onPlayStatusChangedRef.current?.("PAUSED");
        if (videoRef?.current && !playerRef?.current?.ended()) {
          loggerRef.current?.pause();
          const currentTime = player.currentTime();
          if (currentTime) {
            updateLocation(currentTime);
          }
        }
      });

      player.on("seeking", () => {
        loggerRef.current?.startSeek();
      });

      player.on("volumechange", () => {
        const muted = player.muted();
        if (muted !== undefined) {
          // handle muted change from player controls
          handleMutedChange(muted);
          // muted is undefined when the player is disposed
          loggerRef.current?.mutedChanged(muted);
        }
      });

      player.on("seeked", () => {
        loggerRef.current?.endSeeking(player.currentTime() || 0);
        updateLocation(player.currentTime() || 0);
      });

      player.on("timeupdate", () => {
        const currentTime = player.currentTime();
        setLastTime(currentTime || null);
        if (currentTime) {
          loggerRef.current?.timeUpdated(currentTime || 0);
        }
      });

      player.on("stalled", () => {
        loggerRef.current?.stalled();
      });

      player.on("error", () => {
        loggerRef.current?.error(player.error());
      });

      player.on("ended", () => {
        loggerRef.current?.end("playback-ended");
        onPlayStatusChangedRef.current?.("ENDED");
        playerRef.current?.exitFullscreen().catch(() => {});
      });
    }
  }, [
    inline,
    poster,
    source,
    videoRef,
    playerRef,
    lastPlaybackLocation,
    special,
    isPreview,
    updateLocation,
    loggerRef,
    context,
    muted,
    handleMutedChange,
    router,
    isVertical,
    supabase,
    bigPlayButton,
    disableLoop,
    isMobile,
    onPlayStatusChanged,
    mergedPlayerProps,
    isHostDriven,
  ]);

  // synchronize the player's class list with device detection
  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      if (player.isDisposed()) {
        return;
      }
      const videoElement = player.el();

      // Apply device-specific classes
      videoElement.classList.toggle("vjs-mobile", device === "mobile");
      videoElement.classList.toggle("vjs-tablet", device === "tablet");
      videoElement.classList.toggle("vjs-desktop", device === "desktop");
      videoElement.classList.toggle("vjs-landscape", isLandscape);

      // Apply viewport height class for short screens (replaces media query)
      const isShortViewport = window.innerHeight <= 430;
      videoElement.classList.toggle("vjs-short-viewport", isShortViewport);
    }
  }, [device, isLandscape]);

  // update the player's "muted" state when it changes
  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      player.muted(muted);
    }
  }, [muted]);

  // update the player's "playing" state when the prop changes
  useEffect(() => {
    // if the playing prop is undefined, don't do anything
    if (playing === undefined) return;

    const player = playerRef.current;
    if (player) {
      const currentlyPaused = player.paused();
      if (playing && currentlyPaused) {
        // pause if the "playing" prop was set to true AND the player is currently paused
        playWithErrorLogging(player, "playing-prop");
      } else if (!playing && !currentlyPaused) {
        // pause if the "playing" prop was set to false AND the player is currently playing
        player.pause();
      }
    }
  }, [playing]);

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  useEffect(() => {
    // pagehide is more reliable than beforeunload, especially on iOS Safari
    const handleUnload = () => {
      loggerRef.current?.end("pagehide");
    };
    window.addEventListener("pagehide", handleUnload);
    return () => {
      window.removeEventListener("pagehide", handleUnload);

      // End the video session when the user navigates away from the page.
      // (it's okay if both events fire, the videoLogger will dedupe it)
      loggerRef.current?.end("component-unmounted");
    };
  }, [loggerRef]);

  const videoElement = (
    <>
      <div className={cn("vjs-wrapper", className)} ref={videoRef} />
      {!disableOverlays && context && (
        <VideoOverlays
          player={playerRef.current}
          videoState={{
            isPlaying: !playerRef?.current?.paused() || false,
            isEnded: playerRef?.current?.ended() || false,
            currentTime: playerRef?.current?.currentTime() || 0,
            duration: playerRef?.current?.duration(),
            lastTime,
          }}
          context={context}
          onPlayNext={
            onPlayNextOverride ??
            (async (nextSpecial) => {
              specialRef.current = nextSpecial;
              loggerRef.current = new VideoLogger(
                nextSpecial,
                isPreview,
                surface,
                null,
                supabase
              );
              setLoggerVersion((v) => v + 1);

              // Cross-comedian (or unknown-current-comedian) picks navigate
              // instead of swapping the source in place. See `canPlayInPlace`.
              if (!canPlayInPlace(nextSpecial, special)) {
                onSpecialChange?.(nextSpecial);
                return;
              }

              const player = playerRef.current;
              if (!player || !nextSpecial.video_id) {
                return;
              }

              const resp = await getViewingTokenForSpecial(nextSpecial.id);
              if (resp === null || !resp.hasAccess) {
                return;
              }
              player.src(sourceUrl(resp.token));
              playWithErrorLogging(player, "play-next");

              onSpecialChange?.(nextSpecial);
            })
          }
          showEndOverlay={showEndOverlay}
          onCancel={onCancel ?? (() => {})}
          explicitNextSpecial={nextUpSpecial}
          secondaryNextSpecial={secondaryNextSpecial}
          currentSpecialId={special?.id}
          // Persistent player (host-driven) autoplay navigates between specials,
          // so it must only advance to the personalized picks (which carry the
          // routing metadata). Suppress the legacy recommendedVideos fallback —
          // advancing to one swaps source without navigating (stale chrome).
          disableRecommendedFallback={isHostDriven}
        />
      )}
    </>
  );

  if (inline) {
    return videoElement;
  }

  return (
    <div className="bg-black" data-vjs-player>
      {videoElement}
    </div>
  );
};

export default VideoJS;
